// PRD resource-skill-matrix REQ-RSM-011: skill-matrix grid on the resources
// page. Each cell is a (resource × service) intersection showing the active
// skill row's proficiency and credential metadata, or "—" if no skill exists.
// Expired rows render as muted text; suspended rows render in red.

import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { Badge } from "@/components/ui/badge.js";
import { useToast } from "@/components/ui/toast.js";
import type { Proficiency, Resource, ResourceSkill, Service } from "@/lib/types.js";

interface CellState {
  status: "none" | "active" | "expired" | "future" | "suspended";
  skill?: ResourceSkill;
}

function classifySkill(skill: ResourceSkill | undefined, nowMs: number): CellState {
  if (!skill) return { status: "none" };
  if (skill.suspended_at) return { status: "suspended", skill };
  const fromMs = Date.parse(skill.valid_from);
  if (nowMs < fromMs) return { status: "future", skill };
  if (skill.valid_until) {
    const untilMs = Date.parse(skill.valid_until);
    if (nowMs >= untilMs) return { status: "expired", skill };
  }
  return { status: "active", skill };
}

function statusBadgeVariant(status: CellState["status"]): "success" | "warning" | "destructive" | "secondary" {
  if (status === "active") return "success";
  if (status === "expired") return "warning";
  if (status === "future") return "secondary";
  if (status === "suspended") return "destructive";
  return "secondary";
}

export function ResourceSkillsPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const resources = useAsync(() => api.listResources(), [api]);
  const services = useAsync(() => api.listServices(), [api]);
  const skills = useAsync(() => api.listResourceSkills({ active_only: false }), [api]);
  const [editing, setEditing] = useState<{ resource: Resource; service: Service; skill?: ResourceSkill } | null>(null);

  const visibleResources = (resources.data?.data ?? []).filter((r) => !r.deleted_at);
  const visibleServices = (services.data?.data ?? []).filter((s) => !s.deleted_at);
  const skillRows = skills.data?.data ?? [];

  const nowMs = Date.now();
  const cellByPair = useMemo(() => {
    const map = new Map<string, ResourceSkill>();
    // When multiple rows exist for the same (resource, service) pair (e.g.
    // multi-jurisdiction or stacked validity windows), pick the active one
    // with the highest proficiency rank.
    const rank: Record<Proficiency, number> = { learning: 1, qualified: 2, expert: 3 };
    for (const skill of skillRows) {
      const key = `${skill.resource_id}::${skill.service_id}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, skill);
        continue;
      }
      const existingState = classifySkill(existing, nowMs);
      const incomingState = classifySkill(skill, nowMs);
      // Prefer active over non-active; among actives, prefer higher proficiency.
      if (existingState.status === "active" && incomingState.status !== "active") continue;
      if (existingState.status !== "active" && incomingState.status === "active") {
        map.set(key, skill);
        continue;
      }
      if (existingState.status === "active" && incomingState.status === "active") {
        if (rank[skill.proficiency] > rank[existing.proficiency]) {
          map.set(key, skill);
        }
      }
    }
    return map;
  }, [skillRows, nowMs]);

  const loading = resources.loading || services.loading || skills.loading;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Skill matrix</h1>
          <p className="text-sm text-muted-foreground">Per-resource service qualifications, credentials, and validity windows.</p>
        </div>
      </div>
      {loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <div className="overflow-auto rounded border border-border-strong">
          <table className="min-w-full text-sm">
            <thead className="bg-card">
              <tr>
                <th className="sticky left-0 z-10 border-r bg-card shadow-[2px_0_4px_rgba(0,0,0,0.04)] px-3 py-2 text-left font-semibold">Resource \\ Service</th>
                {visibleServices.map((service) => (
                  <th key={service.id} className="px-3 py-2 text-left font-semibold">{service.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleResources.map((resource) => (
                <tr key={resource.id} className="border-t border-border">
                  <td className="sticky left-0 z-10 border-r bg-card shadow-[2px_0_4px_rgba(0,0,0,0.04)] px-3 py-2 font-medium">{resource.name}</td>
                  {visibleServices.map((service) => {
                    const skill = cellByPair.get(`${resource.id}::${service.id}`);
                    const state = classifySkill(skill, nowMs);
                    return (
                      <td key={service.id} className="px-3 py-2 align-top">
                        <button
                          type="button"
                          className="flex flex-col items-start gap-1 rounded px-1 py-0.5 hover:bg-accent/40"
                          onClick={() => setEditing({ resource, service, skill })}
                          aria-label={`Edit skill for ${resource.name} / ${service.name}`}
                        >
                          {state.status === "none" ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <>
                              <Badge variant={statusBadgeVariant(state.status)}>{state.skill?.proficiency ?? state.status}</Badge>
                              <span className="text-xs text-muted-foreground">{labelFor(state)}</span>
                            </>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <SkillDialog
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        editing={editing}
        onSaved={() => {
          skills.reload();
          setEditing(null);
        }}
        onError={(message) => toast({ title: "Save failed", description: message, variant: "destructive" })}
      />
    </div>
  );
}

function labelFor(state: CellState): string {
  if (!state.skill) return "no credential";
  if (state.status === "suspended") return state.skill.suspended_reason ?? "suspended";
  if (state.status === "expired") return "expired";
  if (state.status === "future") return `starts ${shortDate(state.skill.valid_from)}`;
  if (state.skill.valid_until) return `until ${shortDate(state.skill.valid_until)}`;
  return state.skill.credential_id ?? "active";
}

function shortDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

function SkillDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
  onError
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: { resource: Resource; service: Service; skill?: ResourceSkill } | null;
  onSaved: () => void;
  onError: (message: string) => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const [submitting, setSubmitting] = useState(false);

  if (!editing) return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent /></Dialog>;
  const skill = editing.skill;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editing.resource.name} – {editing.service.name}
          </DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const proficiency = String(form.get("proficiency") ?? "qualified") as Proficiency;
            const validUntil = String(form.get("valid_until") ?? "");
            const jurisdiction = String(form.get("jurisdiction") ?? "");
            const credentialId = String(form.get("credential_id") ?? "");
            const credentialBody = String(form.get("credential_body") ?? "");
            // The <input type="datetime-local"> control yields a naked
            // "YYYY-MM-DDTHH:mm" string with no timezone, but the API schema
            // (z.string().datetime()) requires a zone-qualified ISO string.
            // Interpret the input as the browser's local time and serialize
            // to UTC so the backend round-trips a single instant.
            const validUntilIso = validUntil
              ? new Date(validUntil).toISOString()
              : null;
            const body = {
              proficiency,
              valid_until: validUntilIso,
              jurisdiction: jurisdiction || null,
              credential_id: credentialId || null,
              credential_body: credentialBody || null
            };
            setSubmitting(true);
            try {
              if (skill) {
                await api.updateResourceSkill(skill.id, body);
              } else {
                await api.createResourceSkill({
                  resource_id: editing.resource.id,
                  service_id: editing.service.id,
                  ...body
                });
              }
              onSaved();
            } catch (error) {
              onError(String(error));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="proficiency">Proficiency</Label>
            <select
              id="proficiency"
              name="proficiency"
              defaultValue={skill?.proficiency ?? "qualified"}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="learning">learning</option>
              <option value="qualified">qualified</option>
              <option value="expert">expert</option>
            </select>
          </div>
          <Field
            label="Valid until"
            name="valid_until"
            type="datetime-local"
            defaultValue={skill?.valid_until ? skill.valid_until.slice(0, 16) : ""}
          />
          <Field
            label="Jurisdiction"
            name="jurisdiction"
            placeholder="US-CA"
            defaultValue={skill?.jurisdiction ?? ""}
          />
          <Field
            label="Credential ID"
            name="credential_id"
            defaultValue={skill?.credential_id ?? ""}
          />
          <Field
            label="Issuing body"
            name="credential_body"
            defaultValue={skill?.credential_body ?? ""}
          />
          <DialogFooter className="sm:col-span-2 flex flex-wrap gap-2">
            {skill && skill.suspended_at ? (
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    await api.resumeResourceSkill(skill.id);
                    onSaved();
                  } catch (error) {
                    onError(String(error));
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                Resume
              </Button>
            ) : skill ? (
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={async () => {
                  const reason = window.prompt("Reason for suspension?") ?? "";
                  if (!reason) return;
                  setSubmitting(true);
                  try {
                    await api.suspendResourceSkill(skill.id, reason);
                    onSaved();
                  } catch (error) {
                    onError(String(error));
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                Suspend
              </Button>
            ) : null}
            {skill ? (
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    await api.deleteResourceSkill(skill.id);
                    onSaved();
                  } catch (error) {
                    onError(String(error));
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                Delete
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : skill ? "Save" : <><Plus className="mr-1 h-4 w-4" /> Create</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  name,
  ...rest
}: { label: string; name: string } & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} name={name} {...rest} />
    </div>
  );
}
